import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Button,
  tk,
  SheetOverlay,
  SheetPortal,
  SheetContentWrapper,
} from '@sunwebgroup/ui-components';
import { useIntl } from '@/intl';
import { AdvancedMarker, APIProvider, Map } from '@vis.gl/react-google-maps';
import { useDestinationMapData } from '@/scopes/products/pages/components/DestinationSection/hooks';
import { useCMSPageField } from '@/cms/fields';
import { useState } from 'react';
import { MapAccoCard } from './MapAccoCard';
import { styled } from 'styled-components';
import Image from 'next/image';
import inactiveMarker from './assets/location-marker--nearby-accommodation.svg';
import activeMarker from './assets/location-marker--accommodation-active.svg';
import mapPlaceholder from './assets/map-placeholder.svg';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';
import { createPDPClickEvent } from '@/scopes/products/infrastructure/analytics';
import * as React from 'react';

const Container = styled.div`
  position: relative;
  height: 100%;
`;

const SheetContainer = styled.div`
  position: absolute;
  margin: auto;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SheetContentMap = styled(SheetContent)`
  ${SheetContentWrapper} {
    height: 100%;
    width: 100%;
  }
`;

const StyledImage = styled(Image)`
  object-fit: cover;
  border-radius: ${tk('radius_large')};
`;

const DEST_MAP_ACCO_MAP_ID = '46b6aa5f650c9fa7175e5400' as const;

export default function DestinationMap() {
  const intl = useIntl();
  const accommodationId = useCMSPageField('PDP.id');
  const [activeAccoId, setActiveAccoId] = useState<string | null>(accommodationId);
  const { lat, lng } = useCMSPageField('PDP.coordinates');
  const { nearbyAccos } = useDestinationMapData();
  const { sendEvent } = useAnalytics();
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const handleOpenChange = (isOpen: boolean) => {
    setIsSheetOpen(isOpen);
    if (isOpen) {
      sendEvent(
        createPDPClickEvent({
          element: 'Bottom sheet',
          description: 'Destination map bottom sheet opened',
          result: 'Destination map bottom sheet opened',
        })
      );
    } else {
      sendEvent(
        createPDPClickEvent({
          element: 'Bottom sheet',
          description: 'Destination map bottom sheet closed',
          result: 'Destination map bottom sheet closed',
        })
      );
    }
  };

  return (
    <Container>
      <StyledImage src={mapPlaceholder} alt="" fill />
      <SheetContainer>
        <Sheet open={isSheetOpen} onOpenChange={handleOpenChange}>
          <SheetTrigger asChild>
            <Button $hierarchy="secondary">{intl.formatMessage({ id: 'destination_map_open_button' })}</Button>
          </SheetTrigger>
          <SheetPortal>
            <SheetOverlay onClick={() => handleOpenChange} />
            <SheetContentMap $side="bottom">
              <SheetHeader>
                <SheetTitle>{intl.formatMessage({ id: 'destination_map_title' })}</SheetTitle>
                <SheetClose>{intl.formatMessage({ id: 'close' })}</SheetClose>
              </SheetHeader>
              <SheetContentWrapper>
                <APIProvider apiKey={process.env.NEXT_PUBLIC_GMAPS_API_KEY ?? ''}>
                  <Map
                    mapId={DEST_MAP_ACCO_MAP_ID}
                    defaultZoom={12}
                    defaultCenter={{ lat, lng }}
                    reuseMaps
                    fullscreenControl={false}
                    headingInteractionEnabled={false}
                    clickableIcons={false}
                  >
                    <AdvancedMarker
                      position={{ lat, lng }}
                      onClick={() => {
                        setActiveAccoId(accommodationId);
                      }}
                    >
                      <Image src={activeMarker} alt="" />
                    </AdvancedMarker>

                    {nearbyAccos?.map((acco) => (
                      <AdvancedMarker
                        key={acco.id}
                        position={{ lat: acco.lat, lng: acco.lng }}
                        onClick={() => setActiveAccoId(acco.id)}
                      >
                        <Image src={inactiveMarker} alt="" />
                      </AdvancedMarker>
                    ))}
                  </Map>
                </APIProvider>

                {activeAccoId ? <MapAccoCard accoId={activeAccoId} /> : null}
              </SheetContentWrapper>
            </SheetContentMap>
          </SheetPortal>
        </Sheet>
      </SheetContainer>
    </Container>
  );
}
